import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import DownloadIcon from '@mui/icons-material/Download';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import * as htmlToImage from 'html-to-image';

const HighlightImageExportModal = ({ open, onClose, highlight, book, authorProfile }) => {
  // --- STATE ---
  const [showBookInfo, setShowBookInfo] = useState(true);
  const [showAuthorInfo, setShowAuthorInfo] = useState(true);
  const [layout, setLayout] = useState('center'); // 'left', 'right', 'center'
  const [font, setFont] = useState('Gowun Batang'); // 'Gowun Batang', 'Nanum Pen Script'
  const [isExporting, setIsExporting] = useState(false);
  const [isSpreadView, setIsSpreadView] = useState(false); // New state for book spread view
  const exportRef = useRef(null);

  // --- STYLES ---
  const FONT_MAP = {
    'Gowun Batang': '"Gowun Batang", serif',
    'Nanum Pen Script': '"Nanum Pen Script", cursive',
  };

  const LAYOUT_STYLES = {
    left: { justifyContent: 'flex-start', alignItems: 'flex-start', textAlign: 'left', p: '10% 15% 10% 8%' },
    center: { justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: '10% 8%' },
    right: { justifyContent: 'flex-end', alignItems: 'flex-end', textAlign: 'right', p: '10% 8% 10% 15%' },
  };
  
  // --- HANDLERS ---
  const handleDownload = useCallback(() => {
    if (!exportRef.current) return;
    setIsExporting(true);

    htmlToImage.toPng(exportRef.current, {
      quality: 1,
      pixelRatio: 2, // High resolution
      backgroundColor: isSpreadView ? undefined : '#FDFDF6', // Use background from element in spread view
      fontEmbedCss: `
        @import url('https://fonts.googleapis.com/css2?family=Gowun+Batang:wght@400;700&family=Nanum+Pen+Script&display=swap');
      `
    })
    .then((dataUrl) => {
      const link = document.createElement('a');
      link.download = `snaplog-highlight-${new Date().getTime()}.png`;
      link.href = dataUrl;
      link.click();
      setIsExporting(false);
      onClose();
    })
    .catch((error) => {
      console.error('이미지 생성 오류:', error);
      setIsExporting(false);
    });
  }, [exportRef, onClose, isSpreadView]);
  
  if (!highlight || !book) return null;

  const SinglePageView = (
    <Box
      ref={!isSpreadView ? exportRef : null}
      sx={{
        width: '100%',
        aspectRatio: '1 / 1',
        bgcolor: '#FDFDF6',
        color: '#333',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        fontFamily: FONT_MAP[font],
        transition: 'all 0.3s ease',
        ...LAYOUT_STYLES[layout]
      }}
    >
      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ fontSize: font === 'Gowun Batang' ? '1.5rem' : '1.8rem', lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'keep-all' }}>
          {highlight.text}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0, mt: 4 }}>
        {showBookInfo && (
          <Box>
            <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{book.title}</Typography>
            <Typography sx={{ fontSize: '0.9rem', opacity: 0.8 }}>{book.authors?.join(', ')}</Typography>
          </Box>
        )}
        {showAuthorInfo && authorProfile && (
           <Typography sx={{ mt: showBookInfo ? 1.5 : 0, fontSize: '0.9rem', opacity: 0.7 }}>
             @{authorProfile.displayName || '사용자'}
           </Typography>
        )}
      </Box>
    </Box>
  );

  const BookSpreadView = (
    <Box
      ref={isSpreadView ? exportRef : null}
      sx={{
        width: '100%',
        aspectRatio: '16 / 10',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
      }}
    >
      <img
        src="/images/book-spread-background.jpg"
        alt="Open book background"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 1 }}
      />
      <Box
        sx={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2,
          p: '8% 6%',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <Box sx={{
          height: '80%',
          columnCount: 2,
          columnGap: '10%',
          columnFill: 'auto',
          fontFamily: FONT_MAP[font],
          fontSize: font === 'Gowun Batang' ? '1.1rem' : '1.3rem',
          lineHeight: 1.7,
          color: '#3A352F',
          whiteSpace: 'pre-wrap',
          wordBreak: 'keep-all',
          textAlign: 'justify',
          overflow: 'hidden',
        }}>
          {highlight.text}
        </Box>
        
        {showAuthorInfo && authorProfile && (
          <Typography sx={{ position: 'absolute', bottom: '8%', left: '7%', fontSize: '0.8rem', opacity: 0.7, fontFamily: FONT_MAP[font], color: '#3A352F' }}>
            @{authorProfile.displayName || '사용자'}
          </Typography>
        )}

        {showBookInfo && (
          <Box sx={{ position: 'absolute', bottom: '8%', right: '7%', textAlign: 'right', fontFamily: FONT_MAP[font], color: '#3A352F' }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{book.title}</Typography>
            <Typography sx={{ fontSize: '0.8rem', opacity: 0.8 }}>{book.authors?.join(', ')}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        하이라이트 이미지로 내보내기
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, bgcolor: '#f5f5f5' }}>
        {/* --- Controls --- */}
        <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={<Switch checked={isSpreadView} onChange={(e) => setIsSpreadView(e.target.checked)} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoStoriesIcon fontSize="small" />
                  <Typography>책 펼침 모드</Typography>
                </Box>
              }
            />
            <Divider />
            {!isSpreadView && (
              <>
                <Typography variant="overline">레이아웃</Typography>
                <ToggleButtonGroup value={layout} exclusive onChange={(e, v) => v && setLayout(v)} fullWidth>
                  <ToggleButton value="left"><FormatAlignLeftIcon /></ToggleButton>
                  <ToggleButton value="center"><FormatAlignCenterIcon /></ToggleButton>
                  <ToggleButton value="right"><FormatAlignRightIcon /></ToggleButton>
                </ToggleButtonGroup>
              </>
            )}

            <Typography variant="overline">글꼴</Typography>
            <ToggleButtonGroup value={font} exclusive onChange={(e, v) => v && setFont(v)} fullWidth>
              <ToggleButton value="Gowun Batang" sx={{ textTransform: 'none' }}>고운바탕</ToggleButton>
              <ToggleButton value="Nanum Pen Script" sx={{ textTransform: 'none' }}>나눔펜</ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="overline">정보 표시</Typography>
            <FormControlLabel control={<Switch checked={showBookInfo} onChange={(e) => setShowBookInfo(e.target.checked)} />} label="책 제목 / 저자" />
            <FormControlLabel control={<Switch checked={showAuthorInfo} onChange={(e) => setShowAuthorInfo(e.target.checked)} />} label="내 닉네임" />
        </Box>

        {/* --- Preview --- */}
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {isSpreadView ? BookSpreadView : SinglePageView}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button 
          variant="contained" 
          onClick={handleDownload} 
          disabled={isExporting}
          startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
        >
          {isExporting ? '내보내는 중...' : '이미지로 다운로드'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HighlightImageExportModal; 